import React, { useCallback, useEffect } from 'react';
import { ApolloClient, useApolloClient } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import classNames from 'classnames';
import { get, trim } from 'lodash';
import { WithRouterProps } from 'next/dist/client/with-router';
import { withRouter } from 'next/router';
import { Controller, FieldError, FieldErrors, useForm } from 'react-hook-form';
import { defineMessage, FormattedMessage, useIntl } from 'react-intl';
import { connect, useDispatch, useSelector } from 'react-redux';
import { compose } from 'recompose';
import * as yup from 'yup';

import { setSignupFormHover, unsetSignupFormHover } from 'src/actions/app';
import { updateMeta } from 'src/actions/user/me';
import authenticate from 'src/common/authenticate';
import { CountryCodes, Products } from 'src/common/enums';
import useCountryConfig from 'src/common/hooks/useCountryConfig';
import { useProductGtmPayload } from 'src/common/hooks/useProductGtmQuery';
import { useHostNameAndBaseName } from 'src/common/hooks/useUrl';
import { isApplyFlow } from 'src/common/isApplyFlow';
import { getJobLinkPropsFromAsPath, getUtmParams } from 'src/common/links';
import { isNotValidPhoneNumber } from 'src/common/PhoneNumberInput';
import { stringifyPhoneNumber } from 'src/common/phoneNumberInputHelper';
import Checkbox from 'src/components/Checkbox/Checkbox';
import { FloatingLabelInput } from 'src/components/FloatingLabelInput';
import * as Input from 'src/components/FloatingLabelInput/InputComponents.sc';
import { GoogleTagManager } from 'src/components/GoogleTagManager';
import { EmployersLink, TOSLink } from 'src/components/links';
import { PopularHierarchicalLocationSelect } from 'src/components/Select/HierarchicalLocationSelect/PopularHierarchicalLocationSelect';
import { ReduxThunkDispatch } from 'src/global/store';
import { getIsPhoneNumberMandatoryForSignup } from 'src/modules/GeneralConfig/Selectors';
import { StepNames } from 'src/modules/OnboardingGuided/constants';
import { getOnboardingRedirectURL } from 'src/modules/OnboardingGuided/utils';
import { HierarchicalLocation } from 'src/modules/Profile/graphql/hierarchicalLocations';
import { signUp } from 'src/modules/Session/Actions';
import {
  getSignUpError,
  getSignUpSuccess,
  getUserMeta,
} from 'src/modules/Session/Selectors';
import * as Alert from 'src/styles/components/alerts';
import * as SignUpEmail from 'src/styles/pages/onboarding/signupWithEmail';

import { getConfig } from '../ClientConfig';
import { getCountryWithSGFallback } from '../ClientConfig/Selectors';
import { WhatsAppNumberInput } from '../Profile/Components/Form/WhatsAppNumberInput';
import { validateWhatsAppNumberString } from '../Profile/PsychFlatProfilePage/Components/WhatsAppBanner/utils';
import { WhatsAppIcon } from '../Profile/PsychFlatProfilePage/Components/WhatsAppBanner/WhatsAppIcon';
import { SignUpButtonClicked } from '../Session/GTMActions';
import { initialValuesSignUpForm } from './helper';
import {
  EmployerLoginMessages,
  ErrorBodyMessages,
  ErrorHeaderMessages,
  ForgotPasswordLink,
  LoginLink,
} from './messages';
import { WhatsAppBanner } from './SignUpFormPsychFlat.sc';
import { PhoneFormField } from './SignUpPhoneField';

interface SignUpFormPsychFlatProps {
  authenticated: boolean;
  onSubmit: (
    data: SignUpFormPsychFlatData,
    apolloClient?: ApolloClient<object>
  ) => void;
  currentCountry: CountryCodes;
  isPhoneNumberMandatoryForSignup: boolean;
  productGtmPayload: Products;
  initialValues: Partial<SignUpFormPsychFlatData>;
}

type FormValues = {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  location: HierarchicalLocation | undefined;
  phone: string;
  whatsappNumber: string;
  sendUpdates: boolean;
};

function getSignUpErrors(signUpFailErrors: {
  status: number;
  message: string;
}) {
  const emailIsTaken = signUpFailErrors && signUpFailErrors.status === 409;
  let errorHeader = null;
  let errorMessage = null;

  if (signUpFailErrors) {
    if (signUpFailErrors.message === 'Network Error') {
      errorHeader = ErrorHeaderMessages.network;
      errorMessage = ErrorBodyMessages.network;
    } else if (emailIsTaken) {
      errorHeader = ErrorHeaderMessages.emailTaken;
      errorMessage = ErrorBodyMessages.emailTaken;
    } else {
      errorHeader = ErrorHeaderMessages.unknown;
      errorMessage = ErrorBodyMessages.unknown;
    }
  }
  return { errorHeader, errorMessage, emailIsTaken };
}

const SignUpFormPsychFlat: React.FC<
  React.PropsWithChildren<SignUpFormPsychFlatProps & WithRouterProps>
> = ({
  authenticated,
  router,
  currentCountry,
  onSubmit,
  isPhoneNumberMandatoryForSignup,
  initialValues,
}) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const apolloClient = useApolloClient();

  const signUpSuccess = useSelector(getSignUpSuccess);
  const signUpError = useSelector(getSignUpError);
  const userMeta = useSelector(getUserMeta);
  const countryConfig = useCountryConfig();

  const host = useHostNameAndBaseName();

  const redirectToNextPathOrOnboarding = useCallback(() => {
    const { next, nextAs, ...queries } = router.query;
    const asPath = router.asPath;
    const marketingQueryUtmParams = getUtmParams(queries);

    const isApplyingProcess = isApplyFlow(router);
    const isSignUpFromJobDetailsPage =
      typeof router.query.jobTitle === 'string' && !isApplyingProcess;
    /**
     *  @this isApplyingProcess checks if user is redirected to SignUp after pressing ApplyButton
     *    - If unlogged-in user press ApplyButton, we will redirect back to JDP after signing up + display ApplicationModal
     *  @this isSignUpFromJobDetailsPage checks if user presses SignUp in JDP
     *    - If unlogged-in user presses SignUp button in JDP,  we will redirect back to JDP after signing up
     */

    if (isApplyingProcess || isSignUpFromJobDetailsPage) {
      if (isSignUpFromJobDetailsPage) {
        const jobLinkProps = getJobLinkPropsFromAsPath(asPath);
        const url = new URL(`${host}${jobLinkProps.as}`);
        window.open(url.toString(), '_self');
        return;
      }
      router.push(
        { pathname: next?.toString(), query: queries },
        nextAs as any
      );
      return;
    }
    const pathname = getOnboardingRedirectURL(StepNames.JobInterest);

    router.push({ pathname, query: marketingQueryUtmParams });
  }, [host, router]);

  const { productGtmPayload } = useProductGtmPayload();

  const onSubmitSuccess = useCallback(
    (data: FormValues) => {
      const payload: SignUpFormPsychFlatData = {
        ...data,
        whatsappNumber: data.whatsappNumber ?? '',
        gtmPayloadString: productGtmPayload,
      };
      return onSubmit(payload, apolloClient);
    },
    [onSubmit, productGtmPayload, apolloClient]
  );

  useEffect(() => {
    if (authenticated) {
      redirectToNextPathOrOnboarding();
    }
  }, [authenticated, redirectToNextPathOrOnboarding]);

  useEffect(() => {
    return function cleanup() {
      dispatch(unsetSignupFormHover());
    };
  }, [dispatch]);

  const { errorHeader, errorMessage, emailIsTaken } =
    getSignUpErrors(signUpError);

  const onSubmitError = (errors: FieldErrors<FormValues>) => {
    const errorMessages = Object.keys(errors).map(
      (key) => (get(errors, key) as FieldError).message
    );
    dispatch(
      SignUpButtonClicked({ success: false, errors: errorMessages.join(',') })
    );
  };

  const isValidName = (value: string): boolean => {
    if (!value) return true;

    const trimmed = value.trim();
    if (!trimmed) return false;

    const hasLetter = /\p{L}/u.test(trimmed);
    if (!hasLetter) return false;

    const validCharsRegex = /^[\p{L}\s'\-./()]+$/u;

    return validCharsRegex.test(trimmed);
  };

  // E2E hooks live as testIdPrefix props on HierarchicalLocationSelect
  // (testIdPrefix='signup-location') and WhatsAppNumberInput
  // (testIdPrefix='signup-whatsapp') so the cold-start lifecycle spec
  // can drive both widgets end-to-end. The earlier ?skipLocation=1 /
  // ?skipPhone=1 query-param bypasses were removed once the spec stopped
  // needing them.

  const validationShape = {
    firstName: yup
      .string()
      .transform((value) => value?.trim() || '')
      .required(
        intl.formatMessage({
          id: 'errors.first.name.required',
          defaultMessage: 'First name is required.',
        })
      )
      .test(
        'valid-name',
        intl.formatMessage({
          id: 'errors.register.name.invalid',
          defaultMessage:
            'Your name should include letters and not be empty or made only of spaces.',
        }),
        isValidName
      ),
    lastName: yup
      .string()
      .nullable()
      .transform((value) => {
        if (!value) return null;
        return value.trim() || null;
      })
      .test(
        'no-whitespace-only',
        intl.formatMessage({
          id: 'errors.register.name.invalid',
          defaultMessage:
            'Your name should include letters and not be empty or made only of spaces.',
        }),
        function (value, context) {
          const original = (context as any).originalValue;
          if (!original) return true;
          if (original && !original.trim()) return false;
          return true;
        }
      )
      .test(
        'valid-name',
        intl.formatMessage({
          id: 'errors.register.name.invalid',
          defaultMessage:
            'Your name should include letters and not be empty or made only of spaces.',
        }),
        isValidName
      ),
    email: yup
      .string()
      .required(
        intl.formatMessage({
          id: 'errors.email.address.required',
          defaultMessage: 'Email address is required.',
        })
      )
      .email(
        intl.formatMessage({
          id: 'errors.email.address.valid',
          defaultMessage: 'Please use a valid email address.',
        })
      )
      .test(
        'no-plus',
        intl.formatMessage({
          id: 'text-error-validation-plus-in-email',
          defaultMessage:
            'Email must not contain the “+” character, please remove it and try again.',
        }),
        (value) => !value || !value.includes('+')
      ),
    password: yup.string().required(
      intl.formatMessage({
        id: 'errors.password.required',
        defaultMessage: 'Password is required.',
      })
    ),
    location: yup
      .object()
      .shape({
        CountryCode: yup.string().required(
          intl.formatMessage({
            id: 'errors.location.required',
            defaultMessage: 'Your location is required.',
          })
        ),
      })
      .required(
        intl.formatMessage({
          id: 'errors.location.required',
          defaultMessage: 'Your location is required.',
        })
      ),
    ...(countryConfig.whatsAppEnabled
      ? {
          whatsappNumber: yup
            .string()
            .required(
              intl.formatMessage({
                id: 'errors.whatsapp.required',
                defaultMessage: 'WhatsApp number is required.',
              })
            )
            .test(
              'validPhone',
              intl.formatMessage({
                id: 'errors.phone.invalid',
                defaultMessage: 'Please use a valid phone number.',
              }),
              (value) => validateWhatsAppNumberString(value)
            ),
        }
      : {
          phone: yup
            .string()
            .required(
              intl.formatMessage({
                id: 'errors.phone.required',
                defaultMessage: 'Mobile number is required.',
              })
            )
            .test(
              'validPhone',
              intl.formatMessage({
                id: 'errors.phone.invalid',
                defaultMessage: 'Please use a valid phone number.',
              }),
              (value) => {
                return (
                  value &&
                  !isNotValidPhoneNumber(
                    stringifyPhoneNumber(JSON.parse(value))
                  )
                );
              }
            ),
        }),
  };
  const signupSchema = yup.object().shape(validationShape);

  const formDefaultValues = {
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    location: undefined as HierarchicalLocation | undefined,
    phone: '{}',
    whatsappNumber: '',
    sendUpdates: true,
  };
  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    getValues,
  } = useForm<FormValues>({
    mode: 'onBlur',
    defaultValues: {
      ...formDefaultValues,
      ...initialValues,
    } as unknown as FormValues,
    resolver: yupResolver(signupSchema),
  });

  useEffect(() => {
    const redirectIfAuthenticated = async () => {
      if (authenticated) {
        if (Object.keys(userMeta).length) {
          await dispatch(updateMeta(userMeta));
        }
        redirectToNextPathOrOnboarding();
      }
    };

    if (signUpSuccess) {
      dispatch(SignUpButtonClicked({ success: true, errors: '' }));
      redirectIfAuthenticated();
    }
  }, [
    authenticated,
    dispatch,
    redirectToNextPathOrOnboarding,
    signUpSuccess,
    userMeta,
    getValues,
  ]);

  useEffect(() => {
    const { errorMessage } = getSignUpErrors(signUpError);
    if (errorMessage) {
      dispatch(SignUpButtonClicked({ success: false, errors: errorMessage }));
    }
  }, [dispatch, getValues, intl, signUpError]);

  return (
    <SignUpEmail.SignUpFormWrapper>
      <div onMouseEnter={() => dispatch(setSignupFormHover())}>
        <form onSubmit={handleSubmit(onSubmitSuccess, onSubmitError)}>
          <SignUpEmail.SignUpContainer>
            <SignUpEmail.SignUpTwoCols>
              <SignUpEmail.FormWrapper>
                <Controller
                  name="firstName"
                  control={control}
                  render={({ field, fieldState }) => {
                    return (
                      <FloatingLabelInput
                        type="text"
                        id="sign-up-form-first-name"
                        labelId="first.name"
                        labelDefaultMessage="First Name"
                        placeholder={intl.formatMessage(
                          defineMessage({
                            id: 'signup.placeholder.first.name',
                            defaultMessage: 'First Name',
                          })
                        )}
                        normalizeValue={trim}
                        valid={!fieldState.error}
                        touched={fieldState.isTouched && fieldState.isDirty}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        value={field.value}
                      />
                    );
                  }}
                />
                {errors.firstName && (
                  <Input.ErrorMessage>
                    {errors.firstName.type === 'valid-name' ? (
                      <FormattedMessage
                        id="errors.register.name.invalid"
                        defaultMessage="Your name should include letters and not be empty or made only of spaces."
                        tagName="span"
                      />
                    ) : (
                      <FormattedMessage
                        id="errors.first.name.required"
                        defaultMessage="First name is required."
                        tagName="span"
                      />
                    )}
                  </Input.ErrorMessage>
                )}
              </SignUpEmail.FormWrapper>

              <SignUpEmail.FormWrapper>
                <Controller
                  name="lastName"
                  control={control}
                  render={({ field, fieldState }) => (
                    <FloatingLabelInput
                      type="text"
                      id="sign-up-form-last-name"
                      labelId="last.name"
                      labelDefaultMessage="Last Name"
                      placeholder={intl.formatMessage(
                        defineMessage({
                          id: 'signup.placeholder.last.name',
                          defaultMessage: 'Last Name',
                        })
                      )}
                      normalizeValue={trim}
                      valid={!fieldState.error}
                      touched={fieldState.isTouched && fieldState.isDirty}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      value={field.value}
                    />
                  )}
                />
                {errors.lastName && (
                  <Input.ErrorMessage>
                    {
                      <FormattedMessage
                        id="errors.register.name.invalid"
                        defaultMessage="Your name should include letters and not be empty or made only of spaces."
                        tagName="span"
                      />
                    }
                  </Input.ErrorMessage>
                )}
              </SignUpEmail.FormWrapper>
            </SignUpEmail.SignUpTwoCols>
            <SignUpEmail.SignUpTwoCols>
              <SignUpEmail.FormWrapper>
                <Controller
                  name="email"
                  control={control}
                  render={({ field, fieldState }) => (
                    <FloatingLabelInput
                      type="text"
                      id="sign-up-form-email"
                      name="email"
                      labelId="email"
                      labelDefaultMessage="Email Address"
                      placeholder="Email"
                      normalizeValue={trim}
                      valid={!fieldState.error}
                      touched={fieldState.isTouched && fieldState.isDirty}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      value={field.value}
                    />
                  )}
                />
                {errors.email?.type === 'required' && (
                  <Input.ErrorMessage>
                    <FormattedMessage
                      id="errors.email.address.required"
                      defaultMessage="Email address is required."
                      tagName="span"
                    />
                  </Input.ErrorMessage>
                )}
                {errors.email?.type === 'email' && (
                  <Input.ErrorMessage>
                    <span data-cy="signup-invalid-email-message">
                      <FormattedMessage
                        id="errors.email.address.valid"
                        defaultMessage="Please use a valid email address."
                      />
                    </span>
                  </Input.ErrorMessage>
                )}
                {errors.email?.type === 'no-plus' && (
                  <Input.ErrorMessage>
                    <FormattedMessage
                      id="text-error-validation-plus-in-email"
                      defaultMessage="Email must not contain the “+” character, please remove it and try again."
                    />
                  </Input.ErrorMessage>
                )}
              </SignUpEmail.FormWrapper>
              <SignUpEmail.FormWrapper>
                <Controller
                  name="password"
                  control={control}
                  render={({ field, fieldState }) => (
                    <FloatingLabelInput
                      type="password"
                      id="sign-up-form-password"
                      labelId="password"
                      labelDefaultMessage="Password"
                      placeholder={intl.formatMessage(
                        defineMessage({
                          id: 'events.signup.form.create.password',
                          defaultMessage: 'Create Password',
                        })
                      )}
                      valid={!fieldState.error}
                      touched={fieldState.isTouched && fieldState.isDirty}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      value={field.value}
                    />
                  )}
                />
                {errors.password && (
                  <Input.ErrorMessage>
                    <FormattedMessage
                      id="errors.password.required"
                      defaultMessage="Password is required."
                      tagName="span"
                    />
                  </Input.ErrorMessage>
                )}
              </SignUpEmail.FormWrapper>
            </SignUpEmail.SignUpTwoCols>
            <SignUpEmail.SignUpTwoCols>
              <SignUpEmail.FormWrapper>
                <Controller
                  name="location"
                  control={control}
                  render={({ field, fieldState }) => (
                    <PopularHierarchicalLocationSelect
                      countryCode={currentCountry}
                      error={
                        fieldState.error && (
                          <FormattedMessage
                            id="errors.location.required"
                            defaultMessage="Your location is required."
                            tagName="span"
                          />
                        )
                      }
                      testIdPrefix="signup-location"
                      {...field}
                    />
                  )}
                />
              </SignUpEmail.FormWrapper>

              <SignUpEmail.FormWrapper style={{ gap: 4 }}>
                <Choose>
                  <When condition={countryConfig.whatsAppEnabled}>
                    <Controller
                      name="whatsappNumber"
                      control={control}
                      rules={{
                        required: intl.formatMessage({
                          id: 'errors.whatsapp.required',
                          defaultMessage: 'WhatsApp number is required.',
                        }),
                        validate: (value: string | null) => {
                          if (!value) return false;
                          return validateWhatsAppNumberString(value);
                        },
                      }}
                      render={({ field, fieldState }) => {
                        const errorEl = (
                          <>
                            <Choose>
                              <When
                                condition={
                                  fieldState.error?.type === 'required'
                                }
                              >
                                <FormattedMessage
                                  id="errors.whatsapp.required"
                                  defaultMessage="WhatsApp number is required."
                                  tagName="span"
                                />
                              </When>
                              <Otherwise>
                                <FormattedMessage
                                  id="text-use-valid-format"
                                  defaultMessage="Please use a valid format. Example: 8123456789"
                                  tagName="span"
                                />
                              </Otherwise>
                            </Choose>
                          </>
                        );
                        return (
                          <WhatsAppNumberInput
                            {...field}
                            errorMessage={fieldState.error && errorEl}
                            testIdPrefix="signup-whatsapp"
                          />
                        );
                      }}
                    />
                  </When>
                  <Otherwise>
                    <Controller
                      name="phone"
                      control={control}
                      render={({ field, fieldState }) => {
                        const errorEl = (
                          <>
                            <Choose>
                              <When
                                condition={
                                  fieldState.error?.type === 'required'
                                }
                              >
                                <>
                                  <FormattedMessage
                                    id="errors.phone.required"
                                    defaultMessage="Mobile number is required."
                                    tagName="span"
                                  />
                                  &nbsp;
                                  <FormattedMessage
                                    id="signup.mobile.required"
                                    defaultMessage="Employers require this to reach out to you if you are a good match."
                                    tagName="span"
                                  />
                                </>
                              </When>
                              <Otherwise>
                                <span data-cy="invalid_phone_number_toast">
                                  <FormattedMessage
                                    id="errors.phone.invalid"
                                    defaultMessage="Please use a valid phone number."
                                  />
                                </span>
                              </Otherwise>
                            </Choose>
                          </>
                        );
                        return (
                          <PhoneFormField
                            aria-label="phone"
                            isPhoneNumberMandatoryForSignup={
                              isPhoneNumberMandatoryForSignup
                            }
                            {...field}
                            error={fieldState.error && errorEl}
                            touched={fieldState.isTouched}
                          />
                        );
                      }}
                    />
                  </Otherwise>
                </Choose>
                <If condition={countryConfig.whatsAppEnabled}>
                  <WhatsAppBanner>
                    <WhatsAppIcon />
                    <FormattedMessage
                      id="text-whatsapp-number-is-active"
                      defaultMessage="Make sure your WhatsApp number is active since employers might contact you via WhatsApp"
                      tagName="p"
                    />
                  </WhatsAppBanner>
                </If>
              </SignUpEmail.FormWrapper>
            </SignUpEmail.SignUpTwoCols>
          </SignUpEmail.SignUpContainer>
          <If condition={signUpError}>
            <Alert.ErrorMessage>
              <Alert.ErrorMessageHeader>
                {intl.formatMessage(errorHeader)}
              </Alert.ErrorMessageHeader>
              <p>
                {intl.formatMessage(errorMessage)}
                <If condition={emailIsTaken}>
                  <FormattedMessage
                    id="sign-up-form.email-taken.suggestion"
                    defaultMessage="Perhaps you would like to <LoginLink>login</LoginLink> or <ForgotPasswordLink>retrieve your password</ForgotPasswordLink>?"
                    values={{
                      LoginLink,
                      ForgotPasswordLink,
                    }}
                  />
                </If>
              </p>
            </Alert.ErrorMessage>
          </If>

          <SignUpEmail.SubmitWrapper>
            <Controller
              name="sendUpdates"
              control={control}
              render={({ field: { onChange, value, ...otherFields } }) => (
                <Checkbox
                  id="sign-up-form-send-me-updates"
                  name="sendUpdates"
                  checked={value}
                  onClick={(e) => onChange(e.target.checked)}
                  label={intl.formatMessage(
                    defineMessage({
                      id: 'signup.fillMeIn',
                      defaultMessage:
                        'Yes, fill me in on the latest scoops and job opportunities.',
                    })
                  )}
                  {...otherFields}
                />
              )}
            />

            <GoogleTagManager tag="amplitude_signup-click_sign_up_email">
              <SignUpEmail.BtnSubmit
                aria-label="Sign up"
                type="submit"
                className={classNames({ loading: isSubmitting })}
                disabled={isSubmitting}
              >
                <Choose>
                  <When condition={isSubmitting}>
                    <FormattedMessage
                      id="please.wait"
                      defaultMessage="Please wait"
                      tagName="span"
                    />
                    <span>...</span>
                  </When>
                  <Otherwise>
                    <FormattedMessage
                      id="signup.email.button"
                      defaultMessage="Sign up"
                      tagName="span"
                    />
                  </Otherwise>
                </Choose>
              </SignUpEmail.BtnSubmit>
            </GoogleTagManager>

            <FormattedMessage
              {...EmployerLoginMessages.helperText}
              values={{
                link: (
                  <EmployersLink>
                    <FormattedMessage
                      {...EmployerLoginMessages.link}
                      tagName="span"
                    />
                  </EmployersLink>
                ),
              }}
              tagName="span"
            />
          </SignUpEmail.SubmitWrapper>
        </form>
      </div>

      <SignUpEmail.LoginWithSocialMedia>
        <SignUpEmail.TermsWrapper>
          <FormattedMessage
            id="signup.terms"
            defaultMessage="By creating an account here, I understand and agree to the { link }"
            values={{
              link: <TOSLink style={{ fontWeight: 400 }} />,
            }}
            tagName="span"
          />
        </SignUpEmail.TermsWrapper>
      </SignUpEmail.LoginWithSocialMedia>

      <SignUpEmail.AlreadyHaveAccount>
        <FormattedMessage
          tagName="label"
          id="signup.already-have-glints-account"
          defaultMessage="Already have a Glints account?"
        />
        <SignUpEmail.GhostBtnExt href="/login">
          <FormattedMessage id="login" defaultMessage="Login" tagName="span" />
        </SignUpEmail.GhostBtnExt>
      </SignUpEmail.AlreadyHaveAccount>
    </SignUpEmail.SignUpFormWrapper>
  );
};

interface SignUpFormPsychFlatData {
  phone: string;
  whatsappNumber: string;
  firstName: string;
  lastName: string | null;
  location: HierarchicalLocation | undefined;
  email: string;
  password: string;
  sendUpdates: boolean;
  gtmPayloadString: Products;
}

export default compose(
  withRouter,
  connect(
    (state) => ({
      initialValues: initialValuesSignUpForm(getConfig(state)),
      currentCountry: getCountryWithSGFallback(state),
      isPhoneNumberMandatoryForSignup:
        getIsPhoneNumberMandatoryForSignup(state),
    }),
    (dispatch: ReduxThunkDispatch<$TSFixMe>) => ({
      onSubmit: (
        values: SignUpFormPsychFlatData,
        apolloClient?: ApolloClient<object>
      ) =>
        dispatch(
          signUp(
            values.email,
            values.password,
            values.firstName,
            values.lastName,
            values.phone,
            get(values, 'location.id'),
            get(values, 'location.CountryCode'),
            values.sendUpdates,
            values.gtmPayloadString,
            values.whatsappNumber,
            apolloClient
          )
        ),
    })
  )
)(authenticate(SignUpFormPsychFlat, true));
